import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  unreadChatMessagesState,
  isDefaultBackgroundState,
  backgroundTypeState,
  safeAreaInsetsState,
  chatSidebarVisibleState,
  userInfoState,
  activeScopeState,
  scopesState,
} from "../global-state";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import LoadingSpinner from "../common/loading";
import ScriptEditView from "../automation/script-edit-view";
import AutomationHistoryList from "../automation/automation-history-list";
import AutomationHistoryStatistics from "../menu/automation-history-statistics";
import AutomationStatistics from "../menu/automation-statistics";
import OverviewConfigView from "../automation/overview-config-view";
import AutomationListView from "../automation/automation-list-view";
import AutomationTwinListView from "../automation/automation-twin-list-view";
import PremadeGalleryView from "../premade/premade-gallery-view";
import PremadePopularityView from "../premade/premade-popularity-view";
import SharePopularityView from "../premade/share-popularity-view";
import SettingView from "../setting/setting-view";
import NotificationDialogSystem from "./notification-dialog-system";
import DashboardView from "../dashboard/dashboard-view";
import CanvasManage from "../dashboard/canvas-manage";
import { registerMainWidgets } from "../dashboard/main-widget-registry";
import { registerDetailWidgets } from "../dashboard/detail-widget-registry";
import { registerWidgetIcons } from "../dashboard/widget-icon-registry";
import Toolbar from "@mui/material/Toolbar";
import { Typography, IconButton } from "@mhome/ui";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import GridOnIcon from "@mui/icons-material/GridOn";
import GridOffIcon from "@mui/icons-material/GridOff";
import SettingsIcon from "@mui/icons-material/Settings";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import AppBar from "@mui/material/AppBar";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import HorizontalNavigation from "./horizontal-navigation";
import VerticalNavigation from "./vertical-navigation";
import AutomationDebugView from "../automation/automation-debug-view";
import ChatView from "../chat/chat-view";
import AssistantIcon from "@mui/icons-material/Assistant";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../auth/auth-provider";
import IntegrationListView from "../integration/integration-list-view";
import MessagingListView from "../messaging/messaging-list-view";
import TelegramPage from "../messaging/telegram";
import Menu from "../menu/menu";
import Preference from "../menu/preference";
import Scope from "../menu/scope";
import QuickWordsView from "../menu/quick-word";
import IntegrationDetailView from "../integration/integration-detail-view";
import Theme from "../menu/theme";
import Nfc from "../menu/nfc";
import CircadianView from "../menu/circadian";
import TestView from "../menu/test";
import { useMediaQuery, Badge } from "@mui/material";
import {
  Drawer,
  Menu as MuiMenu,
  MenuItem,
  MenuItemIcon as ListItemIcon,
  MenuItemText as ListItemText,
} from "@mhome/ui";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SchedulesListView from "../menu/schedule";
import HubSetting from "../menu/hub-setting";
import PhonePermission from "../menu/phone-permission";
import { DragDropProvider } from "../dashboard/drag-context";
import { matchPath } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import AdaptiveThemeProvider, {
  useIsLeftPartSmallScreen,
} from "../common/adaptive-theme-provider";
import { getBackground } from "../common/adaptive-theme-provider";
import { useToast } from "../common/toast";
import { useUserInfoManager } from "./after-init";
import SpaceDrawer from "./space-drawer";
import DesktopSetting from "../menu/desktop-setting";

const UserApp = ({ history }) => {
  const [unreadChatMessages, setUnreadChatMessages] = useRecoilState(
    unreadChatMessagesState
  );
  const [isDefaultBackground, setIsDefaultBackground] = useRecoilState(
    isDefaultBackgroundState
  );
  const [backgroundType, setBackgroundType] =
    useRecoilState(backgroundTypeState);
  const [notificationStatus, setNotificationStatus] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const path = useLocation();
  const location = useLocation();
  const [drag, setDrag] = useState(false);
  const [dragComplete, setDragComplete] = useState(true);
  const { userAuthState } = useAuth();
  const [showFilter, setShowFilter] = useState(false);
  // Use media query strings directly since new theme system doesn't have breakpoints
  const isMdOrLgScreen = useMediaQuery("@media (min-width:960px)");
  const isSmOrAbove = useMediaQuery("@media (min-width:600px)");
  const isLeftPartSmallScreen = useIsLeftPartSmallScreen();
  const [openAppbar, setOpenAppbar] = useState(true);

  const [notificationListOpen, setNotificationListOpen] = React.useState(false);

  // Add state for dynamic bottom nav visibility
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);

  // Add state for chat sidebar visibility (large screens)
  const [isChatSidebarVisible, setIsChatSidebarVisible] = useRecoilState(
    chatSidebarVisibleState
  );

  // Add state for chat drawer visibility (small screens)
  const [showChatDrawer, setShowChatDrawer] = useState(false);

  // Add state for dropdown menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Add state for space drawer
  const [spaceDrawerOpen, setSpaceDrawerOpen] = useState(false);
  const [currentSpace, setCurrentSpace] = useState(null);
  const [spaceSwitching, setSpaceSwitching] = useState(false);

  // Add API and toast utilities
  const { successToast, errorToast } = useToast();

  // Use centralized user info manager
  const { switchSpace } = useUserInfoManager();
  const userInfo = useRecoilValue(userInfoState);
  const activeScope = useRecoilValue(activeScopeState);
  const scopes = useRecoilValue(scopesState);

  // Get safe area insets from global state
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  // Create refs for components that handle menu actions
  const dashboardRef = useRef();
  const circadianRef = useRef();
  const nfcRef = useRef();

  useEffect(() => {
    registerMainWidgets();
    registerDetailWidgets();
    registerWidgetIcons();
  }, []);

  // Load available spaces and current space info
  useEffect(() => {
    const loadSpacesData = async () => {
      let current = scopes.find((s) => s.id === activeScope);
      if (!current) {
        current = scopes.find((s) => s.defaultActive);
      }
      setCurrentSpace(current);
    };

    loadSpacesData();
  }, [scopes, activeScope]);

  // Update isDefaultBackground state when backgroundType changes
  useEffect(() => {
    setIsDefaultBackground(backgroundType === "default");
  }, [backgroundType, setIsDefaultBackground]);

  // Reset unread count when chat sidebar becomes visible
  useEffect(() => {
    if (isChatSidebarVisible) {
      setUnreadChatMessages(0);
    }
  }, [isChatSidebarVisible, setUnreadChatMessages]);

  // Reset unread count when chat drawer opens on small screens
  useEffect(() => {
    if (showChatDrawer) {
      setUnreadChatMessages(0);
    }
  }, [showChatDrawer, setUnreadChatMessages]);

  // Reset bottom nav visibility when leaving dashboard
  useEffect(() => {
    if (location.pathname !== "/dashboard") {
      setIsBottomNavVisible(true);
    }
  }, [location.pathname]);

  const handleDrag = useCallback(() => {
    // Prevent disabling drag mode if drag is not complete
    if (drag && !dragComplete) {
      return;
    }
    setDrag((prevDrag) => !prevDrag);
  }, [drag, dragComplete]);

  const handleZoomToggle = useCallback(() => {
    setIsBottomNavVisible((prev) => !prev);
  }, []);

  // Common menu action handler that delegates to specific components
  const handleMenuAction = useCallback(
    (type, data) => {
      switch (type) {
        case "navigate":
          history.push(data);
          break;
        default:
          break;
      }
    },
    [history]
  );

  // Handle chat FAB click with responsive behavior
  const handleChatFabClick = useCallback(() => {
    if (isMdOrLgScreen) {
      // On md/lg screens, toggle the sidebar
      setIsChatSidebarVisible((prev) => !prev);
    } else {
      // On small screens, show the drawer instead of navigating
      setShowChatDrawer(true);
    }
  }, [isMdOrLgScreen]);

  // Handle closing chat drawer
  const handleCloseChatDrawer = () => {
    setShowChatDrawer(false);
  };

  // Handle assistant interaction - close drawer on small screens
  const handleAssistantInteraction = () => {
    console.log("handleAssistantInteraction");
    if (showChatDrawer) {
      setShowChatDrawer(false);
    }
  };

  // Handle space drawer toggle
  const handleSpaceHeaderClick = () => {
    setSpaceDrawerOpen(true);
  };

  const handleSpaceDrawerClose = () => {
    setSpaceDrawerOpen(false);
  };

  // Handle space switching
  const handleSpaceSwitch = async (newSpace) => {
    if (!currentSpace || newSpace.id === currentSpace.id) {
      setSpaceDrawerOpen(false);
      return;
    }

    setSpaceSwitching(true);
    try {
      const result = await switchSpace(currentSpace.id, newSpace.id);
      if (result.success) {
        setCurrentSpace(newSpace);
        successToast("Switched to " + newSpace.name);
      } else {
        errorToast(result.error || "Failed to switch space");
      }
    } catch (error) {
      console.error("Error switching space:", error);
      errorToast("Failed to switch space");
    } finally {
      setSpaceSwitching(false);
      setSpaceDrawerOpen(false);
    }
  };

  // Handle dropdown menu
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (action) => {
    handleMenuClose();

    // Delegate menu actions to the appropriate component
    if (
      currentRoute.path === "/dashboard" &&
      dashboardRef.current?.handleMenuAction
    ) {
      dashboardRef.current.handleMenuAction(action);
    }
  };

  // Handle add button click
  const handleAddClick = () => {
    if (currentRoute.path === "/circadian" && circadianRef.current?.handleAdd) {
      circadianRef.current.handleAdd();
    } else if (currentRoute.path === "/nfc" && nfcRef.current?.handleAdd) {
      nfcRef.current.handleAdd();
    }
  };

  // Handle query button click
  const handleQueryClick = () => {
    if (currentRoute.path === "/nfc" && nfcRef.current?.handleQuery) {
      nfcRef.current.handleQuery();
    }
  };

  // Get menu items based on current route
  const getMenuItems = () => {
    const items = [];

    if (currentRoute.path === "/dashboard") {
      // Add card item
      items.push({
        key: "add-card",
        label: "Add card",
        icon: <AddIcon />,
        action: "add-card",
        disabled: drag,
      });

      // Drag toggle item
      items.push({
        key: "toggle-drag",
        label: "Change Layout",
        icon: drag ? (
          <GridOffIcon style={{ color: "hsl(var(--warning))" }} />
        ) : (
          <GridOnIcon />
        ),
        action: "toggle-drag",
        disabled: drag && !dragComplete,
      });

      // Dashboard specific items
      items.push({
        key: "canvas-manage",
        label: "Manage Canvas",
        icon: <DashboardCustomizeIcon />,
        action: "canvas-manage",
      });
    }

    return items;
  };

  const devPath = ["/automation/script", "/automation/debug"];

  const routes = [
    {
      path: "/dashboard",
      exact: true,
      component: DashboardView,
      title: "Dashboard",
      showBack: false,
      showNav: true,
      showMenu: true,
      showNotification: true,
      showAssistant: true,
      zoomIcon: true,
      props: {
        drag,
        dragClose: handleDrag,
        setIsBottomNavVisible: setIsBottomNavVisible,
        isChatSidebarVisible: isChatSidebarVisible,
        onTempStackChange: (hasItems) => setDragComplete(!hasItems),
        onMenuAction: handleMenuAction,
        ref: dashboardRef,
      },
    },
    {
      path: "/dashboard/manage",
      exact: true,
      component: CanvasManage,
      title: "Canvas Manage",
      showBack: true,
      showNav: false,
      backPath: "/dashboard",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/script",
      exact: true,
      title: "Automation Script",

      showBack: true,
      component: ScriptEditView,
      backPath: "/automation/list",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/debug",
      exact: true,
      title: "Automation Debug",

      showBack: true,
      component: AutomationDebugView,
      backPath: "/automation/list",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/history",
      exact: true,
      title: "Automation History",

      showBack: true,
      component: AutomationHistoryList,
      backPath: "/automation/list",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/history/statistics",
      exact: true,
      title: "History Statistics",

      showBack: true,
      component: AutomationHistoryStatistics,
      backPath: "/automation/list",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/overview",
      exact: true,
      title: "Automation",

      showBack: true,
      component: OverviewConfigView,
      backPath: "/automation/list",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/list",
      exact: true,
      title: "Automation",

      showBack: false,
      showNotification: true,
      showNav: true,
      component: AutomationListView,
      showAssistant: true,
      props: {
        showFilter: showFilter,
        setShowFilter: setShowFilter,
        isChatSidebarVisible: isChatSidebarVisible,
      },
    },
    {
      path: "/automation/twin/list",
      exact: true,
      title: "Card Automation",

      showBack: true,
      showNotification: false,
      showNav: false,
      component: AutomationTwinListView,
      backPath: "/dashboard",
      showAssistant: true,
    },
    {
      path: "/gallery",
      exact: true,
      title: "Inspiration Gallery",

      showBack: false,
      showNotification: true,
      showNav: true,
      component: PremadeGalleryView,
      showAssistant: true,
      props: {
        showFilter: showFilter,
        setShowFilter: setShowFilter,
      },
    },
    {
      path: "/premade/popularity",
      exact: true,
      title: "Premade Popularity",

      showBack: true,
      component: PremadePopularityView,
      backPath: "/gallery",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/share/popularity",
      exact: true,
      title: "Share Popularity",

      showBack: true,
      component: SharePopularityView,
      backPath: "/gallery",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/setting",
      exact: true,
      title: "Setting",

      showBack: true,
      component: SettingView,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/menu",
      exact: true,
      title: "Menu",

      showBack: false,
      showNotification: true,
      showNav: true,
      component: Menu,
      showAssistant: true,
      props: { showFilter: showFilter, setShowFilter: setShowFilter },
    },
    {
      path: "/space",
      exact: true,
      title: "Space",

      showBack: true,
      component: Scope,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/automation/statistics",
      exact: true,
      title: "Automation Statistics",

      showBack: true,
      component: AutomationStatistics,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/integrations",
      exact: true,
      title: "Integrations",

      showBack: true,
      showNotification: true,
      component: IntegrationListView,
      showAssistant: true,
      props: { showFilter: showFilter, setShowFilter: setShowFilter },
      backPath: "/menu",
    },
    {
      path: "/quickwords",
      exact: true,
      title: "Quick Commands",

      showBack: true,
      showNotification: true,
      component: QuickWordsView,
      showAssistant: true,
      props: { showFilter: showFilter, setShowFilter: setShowFilter },
      backPath: "/menu",
    },
    {
      path: "/preference",
      exact: true,
      title: "Preference",
      showBack: true,
      component: Preference,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/theme",
      exact: true,
      title: "Theme",
      showBack: true,
      component: Theme,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/nfc",
      exact: true,
      title: "NFC",
      showBack: true,
      component: Nfc,
      backPath: "/menu",
      showAdd: true,
      showQuery: true,
      showAssistant: true,
      showNotification: true,
      props: {
        ref: nfcRef,
      },
    },
    {
      path: "/circadian",
      exact: true,
      title: "Circadian Schedule",
      showBack: true,
      component: CircadianView,
      backPath: "/menu",
      showAdd: true,
      showAssistant: true,
      showNotification: true,
      props: {
        ref: circadianRef,
      },
    },
    {
      path: "/test",
      exact: true,
      title: "Dev Test",
      showBack: true,
      component: TestView,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/integration",
      exact: true,
      title: "Integration",

      showBack: true,
      component: IntegrationDetailView,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
      props: {
        showFilter: showFilter,
        setShowFilter: setShowFilter,
        isChatSidebarVisible,
      },
    },
    {
      path: "/messaging",
      exact: true,
      title: "Messaging Apps",

      showBack: true,
      showNotification: true,
      component: MessagingListView,
      showAssistant: true,
      backPath: "/menu",
    },
    {
      path: "/messaging/telegram",
      exact: true,
      title: "Telegram",

      showBack: true,
      component: TelegramPage,
      backPath: "/messaging",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/schedules",
      exact: true,
      title: "Schedule",

      showBack: true,
      component: SchedulesListView,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/hub/setting",
      exact: true,
      title: "Hub Setting",

      showBack: true,
      component: HubSetting,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/desktop/setting",
      exact: true,
      title: "Desktop Setting",
      showBack: true,
      component: DesktopSetting,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
    {
      path: "/phone/setting",
      exact: true,
      title: "Phone Setting",

      showBack: true,
      component: PhonePermission,
      backPath: "/menu",
      showAssistant: true,
      showNotification: true,
    },
  ];

  useEffect(() => {
    // Hide AppBar on small screens when viewing specific setting pages
    if (
      location.pathname === "/setting" &&
      location.hash &&
      isLeftPartSmallScreen
    ) {
      setOpenAppbar(false);
    } else {
      setOpenAppbar(true);
    }
  }, [location, isLeftPartSmallScreen, setOpenAppbar]);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action === "POP" && notificationListOpen) {
        setNotificationListOpen(false);
      }
    });

    return () => {
      unlisten();
    };
  }, [history, notificationListOpen]);

  // Handle browser/mobile back button when chat drawer is open
  useEffect(() => {
    const handlePopState = (event) => {
      if (showChatDrawer && !isMdOrLgScreen) {
        // Prevent the default back navigation
        event.preventDefault();
        event.stopPropagation();
        // Close the chat drawer instead
        setShowChatDrawer(false);
      }
    };

    if (showChatDrawer && !isMdOrLgScreen) {
      // Listen for popstate events (back button)
      window.addEventListener("popstate", handlePopState);

      // Add a dummy history entry when chat drawer opens to allow back navigation
      const currentPath = location.pathname + location.search + location.hash;
      if (history.location.pathname === currentPath.split("?")[0]) {
        history.push(currentPath);
      }
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [showChatDrawer, isMdOrLgScreen]);

  const getCurrentRoute = () => {
    let r;
    try {
      r = routes.find((route) =>
        matchPath(path.pathname, { path: route.path, exact: !!route.exact })
      );
      if (!r) {
        r = routes[0];
      }
    } catch (error) {
      r = routes[0];
    }

    if (r.path === "/integration") {
      r = {
        ...r,
        hash: location.hash,
      };
    }

    return r;
  };

  const currentRoute = getCurrentRoute();

  useEffect(() => {
    if (!history) {
      return;
    }
    setCurTab(getCurrentTab());
  }, [history, location.pathname]);

  const handleNotificationIconClick = () => {
    setNotificationListOpen(!notificationListOpen);
  };

  const getCurrentTitle = () => {
    return currentRoute ? currentRoute.title : "";
  };

  const [curTab, setCurTab] = React.useState(-1);

  const getCurrentTab = () => {
    if (location.pathname === "/dashboard") return 0;
    if (location.pathname === "/automation/list") return 1;
    if (location.pathname === "/gallery") return 2;
    if (location.pathname === "/menu") return 3;
    return curTab >= 0 ? curTab : 0; // Keep current tab for unknown routes, default to dashboard if uninitialized
  };

  const handleSettingsClick = () => {
    history.push("/setting");
  };

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        background: getBackground(backgroundType),
        display: "flex",
        paddingTop: `${safeAreaInsets.top}px`,
        fontFamily: "inherit",
      }}
    >
      {!userAuthState ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            fontFamily: "inherit",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* Main app container */}
          <div
            style={{
              width:
                isMdOrLgScreen && isChatSidebarVisible
                  ? "calc(100vw - 400px)"
                  : "100%", // Use 100% instead of 100vw to prevent horizontal overflow
              height: `calc(100vh - ${safeAreaInsets.top}px)`,
              transition: "width 0.3s ease",
              display: "flex",
              flexDirection: "column",
              fontFamily: "inherit",
              overflowX: "hidden", // Prevent horizontal overflow
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                paddingBottom:
                  currentRoute.showNav && isBottomNavVisible && !isSmOrAbove
                    ? "64px"
                    : "10px",
                paddingLeft: isSmOrAbove ? "60px" : 0,
                fontFamily: "inherit",
              }}
            >
              {openAppbar && (
                <AppBar
                  position="fixed"
                  style={{
                    background: "transparent",
                    border: "none",
                    boxShadow: "none",
                    borderRadius: 0,
                    borderBottom: "none",
                    width:
                      isMdOrLgScreen && isChatSidebarVisible
                        ? "calc(100vw - 460px)" // Account for both vertical nav (60px) and chat sidebar (400px)
                        : isSmOrAbove
                        ? "calc(100vw - 60px)" // Account for vertical nav only
                        : "100vw",
                    left: isSmOrAbove ? "60px" : 0,
                    top: `${safeAreaInsets.top}px`,
                    transition: "width 0.3s ease, left 0.3s ease",
                    height: "56px",
                  }}
                >
                  <Toolbar
                    style={{
                      minHeight: "56px !important",
                      height: "56px",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    }}
                    className="[&_.MuiIconButton-root]:p-2 [&_.MuiIconButton-root]:ml-1 [&_.MuiSvgIcon-root]:text-[1.625rem]"
                  >
                    <>
                      {currentRoute && currentRoute.showBack && (
                        <IconButton
                          color="inherit"
                          aria-label="overview"
                          edge="start"
                          style={{
                            marginRight: 0,
                            marginLeft: 0,
                            transform: "translateX(-12px)",
                          }}
                          onClick={() => {
                            // Check if we should go back or use default backPath
                            const shouldUseBackPath = history.length <= 2;
                            if (shouldUseBackPath) {
                              // Use default backPath if no meaningful previous page
                              history.replace(currentRoute.backPath);
                            } else if (
                              location.pathname.startsWith("/setting")
                            ) {
                              // Special case for settings page
                              history.push("/menu");
                            } else {
                              // Safe to go back to previous page
                              history.goBack();
                            }
                          }}
                        >
                          <ArrowBackIcon
                            style={{
                              color: "hsl(var(--foreground))",
                            }}
                          />
                        </IconButton>
                      )}
                      {currentRoute.path === "/dashboard" && currentSpace ? (
                        <div
                          style={{
                            flexGrow: 1,
                            display: "flex",
                            alignItems: "center",
                            fontFamily: "inherit",
                          }}
                        >
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSpaceHeaderClick();
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              WebkitTapHighlightColor: "transparent",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "0.8";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "1";
                            }}
                          >
                            <HomeIcon
                              style={{
                                color: "hsl(var(--foreground))",
                                fontSize: "1.375rem",
                                marginRight: "8px",
                              }}
                            />
                            <Typography
                              variant="h6"
                              component="div"
                              noWrap
                              className="overflow-hidden text-ellipsis max-w-full"
                              style={{
                                color: "hsl(var(--foreground))",
                                fontSize: "1.25rem",
                                lineHeight: "1.2",
                                fontWeight: 600,
                                marginRight: "4px",
                              }}
                            >
                              {currentSpace.name}
                            </Typography>
                            <ExpandMoreIcon
                              style={{
                                color: "hsl(var(--foreground))",
                                fontSize: "1.375rem",
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <Typography
                          variant="h6"
                          component="div"
                          noWrap
                          className="flex-1 overflow-hidden text-ellipsis max-w-full"
                          style={{
                            color: "hsl(var(--foreground))",
                            fontSize: "1.125rem",
                            lineHeight: "1.2",
                            fontWeight: 500,
                          }}
                        >
                          {getCurrentTitle()}
                        </Typography>
                      )}
                      {(currentRoute.path === "/gallery" ||
                        currentRoute.path === "/automation/list" ||
                        currentRoute.path === "/integrations" ||
                        currentRoute.path === "/quickwords" ||
                        (currentRoute.path === "/integration" &&
                          currentRoute.hash !== "#info")) && (
                        <IconButton
                          color="inherit"
                          aria-label="filter"
                          onClick={() => {
                            if (showFilter) {
                              if (
                                location.pathname.startsWith("/automation/list")
                              ) {
                                localStorage.removeItem(
                                  "filterParams-recurring"
                                );
                              }
                            }
                            setShowFilter(!showFilter);
                          }}
                        >
                          <FilterListIcon
                            style={{
                              color: showFilter
                                ? "hsl(var(--warning))"
                                : "hsl(var(--foreground))",
                            }}
                          />
                        </IconButton>
                      )}

                      {/* Query button for routes with showQuery */}
                      {currentRoute && currentRoute.showQuery && (
                        <IconButton
                          color="inherit"
                          aria-label="query"
                          onClick={handleQueryClick}
                        >
                          <SearchIcon
                            style={{
                              color: "hsl(var(--foreground))",
                            }}
                          />
                        </IconButton>
                      )}

                      {/* Add button for routes with showAdd */}
                      {currentRoute && currentRoute.showAdd && (
                        <IconButton
                          color="inherit"
                          aria-label="add"
                          onClick={handleAddClick}
                        >
                          <AddIcon
                            style={{
                              color: "hsl(var(--foreground))",
                            }}
                          />
                        </IconButton>
                      )}

                      {/* Dropdown menu for routes with showMenu */}
                      {currentRoute && currentRoute.showMenu && (
                        <>
                          {/* Show direct exit icon when drag mode is active */}
                          {drag && (
                            <IconButton
                              color="secondary"
                              aria-label="disable grid"
                              onClick={handleDrag}
                              disabled={!dragComplete}
                              style={{
                                opacity: !dragComplete ? 0.5 : 1,
                              }}
                            >
                              <GridOffIcon
                                style={{
                                  color: "hsl(var(--warning))",
                                }}
                              />
                            </IconButton>
                          )}

                          {/* Show dropdown menu when neither drag nor edit mode is active */}
                          {!drag && (
                            <>
                              <IconButton
                                aria-label="menu"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMenuClick(e);
                                }}
                              >
                                <MoreHorizIcon
                                  style={{
                                    color: "hsl(var(--foreground))",
                                  }}
                                />
                              </IconButton>
                              <MuiMenu
                                anchorEl={menuAnchorEl}
                                open={isMenuOpen}
                                onClose={handleMenuClose}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                                PaperProps={{
                                  style: {
                                    minWidth: "180px",
                                    borderRadius: "16px",
                                  },
                                }}
                              >
                                {getMenuItems().map((item) => (
                                  <MenuItem
                                    key={item.key}
                                    onClick={() =>
                                      handleMenuItemClick(item.action)
                                    }
                                    disabled={item.disabled}
                                    style={{
                                      opacity: item.disabled ? 0.5 : 1,
                                    }}
                                  >
                                    <ListItemIcon
                                      className="[&_.MuiSvgIcon-root]:text-[1.25rem]"
                                      style={{
                                        minWidth: "36px",
                                      }}
                                    >
                                      {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.label} />
                                  </MenuItem>
                                ))}
                              </MuiMenu>
                            </>
                          )}
                        </>
                      )}

                      {currentRoute.showNotification && !isSmOrAbove && (
                        <IconButton
                          color={
                            (notificationStatus & 4) > 0
                              ? "error"
                              : (notificationStatus & 2) > 0
                              ? "warning"
                              : "text.primary"
                          }
                          aria-label="notifications"
                          onClick={handleNotificationIconClick}
                        >
                          {notificationCount > 0 ? (
                            <Badge
                              badgeContent={notificationCount}
                              color="warning"
                              overlap="circular"
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              max={99}
                            >
                              <NotificationsOutlinedIcon />
                            </Badge>
                          ) : (
                            <NotificationsOutlinedIcon
                              style={{
                                color: "hsl(var(--foreground))",
                              }}
                            />
                          )}
                        </IconButton>
                      )}

                      {/* Chat icon in app bar - positioned after notifications */}
                      {currentRoute.showAssistant && !isSmOrAbove && (
                        <>
                          {/* SVG gradient definition for assistant icon */}
                          <svg
                            width="0"
                            height="0"
                            style={{ position: "absolute" }}
                          >
                            <defs>
                              <linearGradient
                                id="assistantGradientMobile"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                              >
                                <stop
                                  offset="0%"
                                  style={{
                                    stopColor: "#475B63",
                                    stopOpacity: 1,
                                  }}
                                />
                                <stop
                                  offset="100%"
                                  style={{
                                    stopColor: "#E6A07C",
                                    stopOpacity: 1,
                                  }}
                                />
                              </linearGradient>
                            </defs>
                          </svg>
                          <IconButton
                            color="inherit"
                            aria-label="assistant"
                            onClick={handleChatFabClick}
                          >
                            {unreadChatMessages > 0 ? (
                              <Badge
                                badgeContent={unreadChatMessages}
                                color="error"
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                                max={99}
                              >
                                <AssistantIcon
                                  style={{
                                    fontSize: "24px",
                                    color: showChatDrawer
                                      ? "hsl(var(--primary-foreground))"
                                      : "url(#assistantGradientMobile)",
                                    fill: !showChatDrawer
                                      ? "url(#assistantGradientMobile)"
                                      : undefined,
                                  }}
                                />
                              </Badge>
                            ) : (
                              <AssistantIcon
                                style={{
                                  fontSize: "24px",
                                  color: showChatDrawer
                                    ? "hsl(var(--primary-foreground))"
                                    : "url(#assistantGradientMobile)",
                                  fill: !showChatDrawer
                                    ? "url(#assistantGradientMobile)"
                                    : undefined,
                                }}
                              />
                            )}
                          </IconButton>
                        </>
                      )}

                      {currentRoute &&
                        currentRoute.zoomIcon &&
                        !isSmOrAbove && (
                          <IconButton
                            color="inherit"
                            aria-label={
                              isBottomNavVisible ? "zoom out" : "zoom in"
                            }
                            onClick={handleZoomToggle}
                          >
                            {isBottomNavVisible ? (
                              <ZoomOutMapIcon
                                style={{
                                  color: "hsl(var(--foreground))",
                                }}
                              />
                            ) : (
                              <ZoomInMapIcon
                                style={{
                                  color: "hsl(var(--foreground))",
                                }}
                              />
                            )}
                          </IconButton>
                        )}
                    </>

                    {location.pathname === "/menu" && !isSmOrAbove && (
                      <IconButton
                        aria-controls="account-menu"
                        aria-haspopup="true"
                        onClick={handleSettingsClick}
                        size="large"
                        edge="end"
                        color="inherit"
                      >
                        <SettingsIcon
                          style={{
                            color: "hsl(var(--foreground))",
                          }}
                        />
                      </IconButton>
                    )}
                  </Toolbar>
                </AppBar>
              )}

              {openAppbar && (
                <div
                  style={{
                    height: "56px",
                    flexShrink: 0,
                  }}
                />
              )}
              <div
                style={{
                  flexGrow: 1,
                  background: "transparent",
                  width: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  fontFamily: "inherit",
                }}
              >
                <Switch>
                  <Route
                    path="/automation/shared/:code"
                    render={({ match }) => (
                      <Redirect
                        to={`/automation/overview?sharedId=${match.params.code}`}
                      />
                    )}
                  />
                  {routes
                    .filter((route) => {
                      return (
                        devPath.indexOf(route.path) === -1 ||
                        userInfo?.setting?.engAccount
                      );
                    })
                    .map((route, index) => (
                      <Route
                        key={index}
                        path={route.path}
                        exact={route.exact}
                        render={(props) => (
                          <AdaptiveThemeProvider>
                            <DragDropProvider>
                              <route.component {...props} {...route.props} />
                            </DragDropProvider>
                          </AdaptiveThemeProvider>
                        )}
                      />
                    ))}
                  <Route path="*">
                    <Redirect to="/dashboard" />
                  </Route>
                </Switch>
              </div>
              {((currentRoute && currentRoute.showNav && isBottomNavVisible) ||
                isSmOrAbove) &&
                (isSmOrAbove ? (
                  <VerticalNavigation
                    curTab={curTab}
                    setCurTab={setCurTab}
                    history={history}
                    notificationStatus={notificationStatus}
                    notificationCount={notificationCount}
                    onNotificationClick={handleNotificationIconClick}
                    isChatVisible={
                      isMdOrLgScreen ? isChatSidebarVisible : showChatDrawer
                    }
                    onChatToggle={() => {
                      if (isMdOrLgScreen) {
                        // On md+ screens, toggle the sidebar
                        setIsChatSidebarVisible(!isChatSidebarVisible);
                      } else {
                        // On sm screens, open the drawer
                        setShowChatDrawer(true);
                      }
                    }}
                  />
                ) : (
                  <HorizontalNavigation
                    curTab={curTab}
                    setCurTab={setCurTab}
                    history={history}
                    isBottomNavVisible={isBottomNavVisible}
                    isMdOrLgScreen={isMdOrLgScreen}
                    isChatSidebarVisible={isChatSidebarVisible}
                  />
                ))}
              <NotificationDialogSystem
                listOpen={notificationListOpen}
                setListOpen={setNotificationListOpen}
                setNotificationStatus={setNotificationStatus}
                setNotificationCount={setNotificationCount}
              />
            </div>
          </div>

          {/* Chat sidebar for md/lg screens */}
          {isMdOrLgScreen && (
            <div
              style={{
                width: "400px",
                height: `calc(100vh - ${safeAreaInsets.top}px - ${safeAreaInsets.bottom}px)`,
                backgroundColor: document.documentElement.classList.contains(
                  "dark"
                )
                  ? "rgba(0, 0, 0, 0.3)"
                  : "rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px 0 0 16px",
                display: "flex",
                flexDirection: "column",
                zIndex: "var(--z-index-app-bar)",
                position: "fixed",
                right: `${safeAreaInsets.right}px`,
                top: `${safeAreaInsets.top}px`,
                transform: isChatSidebarVisible
                  ? "translateX(0)"
                  : "translateX(100%)",
                transition: "transform 0.3s ease",
                overflow: "hidden",
                fontFamily: "inherit",
              }}
            >
              {/* Chat sidebar header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 16px",
                  background: "transparent",
                  minHeight: "56px",
                  height: "56px",
                  fontFamily: "inherit",
                }}
              >
                <IconButton
                  onClick={() => setIsChatSidebarVisible(false)}
                  edge="start"
                  className="mr-4 [&_.MuiSvgIcon-root]:text-[1.375rem]"
                  style={{
                    padding: "8px",
                    color: "hsl(var(--foreground))",
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  className="flex-1 overflow-hidden text-ellipsis max-w-full"
                  style={{
                    color: "hsl(var(--foreground))",
                    fontSize: "1.125rem",
                    lineHeight: "1.2",
                    fontWeight: 500,
                  }}
                >
                  Assistant
                </Typography>
              </div>

              {/* Chat content */}
              <div
                style={{
                  flex: 1,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <ChatView
                  isChatVisible={isChatSidebarVisible}
                  onAssistantInteraction={null}
                />
              </div>
            </div>
          )}

          {/* Global Chat drawer for small screens */}
          {!isMdOrLgScreen && (
            <Drawer
              anchor="right"
              open={showChatDrawer}
              onClose={handleCloseChatDrawer}
              transitionDuration={300}
              keepMounted={true}
              style={{
                zIndex: "var(--z-index-drawer)",
              }}
              PaperProps={{
                style: {
                  borderRadius: 0,
                  width: "100vw",
                  background: isDefaultBackground
                    ? `hsl(var(--background))`
                    : getBackground(backgroundType),
                  transition:
                    "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important",
                },
              }}
              BackdropProps={{
                style: {
                  transition:
                    "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important",
                },
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100vh",
                  paddingTop: `${safeAreaInsets.top}px`,
                  display: "flex",
                  flexDirection: "column",
                  fontFamily: "inherit",
                }}
              >
                {/* Chat drawer header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    background: "transparent",
                    minHeight: "56px",
                    height: "56px",
                    fontFamily: "inherit",
                  }}
                >
                  <IconButton
                    onClick={handleCloseChatDrawer}
                    edge="start"
                    className="mr-4 [&_.MuiSvgIcon-root]:text-[1.375rem]"
                    style={{
                      padding: "8px",
                      color: "hsl(var(--foreground))",
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography
                    variant="h6"
                    className="flex-1 overflow-hidden text-ellipsis max-w-full"
                    style={{
                      color: "hsl(var(--foreground))",
                      fontSize: "1.125rem",
                      lineHeight: "1.2",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    Assistant
                  </Typography>
                </div>

                {/* Chat content */}
                <div
                  style={{
                    flex: 1,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <ChatView
                    isChatVisible={showChatDrawer}
                    onAssistantInteraction={handleAssistantInteraction}
                  />
                </div>
              </div>
            </Drawer>
          )}

          {/* Space switching drawer */}
          <SpaceDrawer
            open={spaceDrawerOpen}
            onClose={handleSpaceDrawerClose}
            currentSpace={currentSpace}
            onSpaceSwitch={handleSpaceSwitch}
            spaceSwitching={spaceSwitching}
          />
        </>
      )}
    </div>
  );
};

export default UserApp;
